import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import patientAuthService from '@/lib/patient-auth'

interface LoginProps {
  return_to?: string
}

const loginSchema = z.object({
  email: z.string().min(1, 'Email or phone number is required'),
  password: z.string().min(1, 'Password is required'),
})

type LoginFormData = z.infer<typeof loginSchema>

export default function Login({ return_to }: LoginProps) {
  const [error, setError] = useState('')
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)

  const { register, handleSubmit, formState: { errors: formErrors, isSubmitting } } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  // Check if patient is already authenticated with existing token
  useEffect(() => {
    const checkExistingAuth = () => {
      const token = patientAuthService.getToken()

      if (token) {
        // Patient has a token, redirect to progress page
        const redirectUrl = return_to || '/patients/progress'
        router.visit(redirectUrl, {
          replace: true,
          onError: () => {
            // If the visit fails (e.g., 401), clear the invalid token
            patientAuthService.clearTokens()
            setIsCheckingAuth(false)
          },
        })
      } else {
        setIsCheckingAuth(false)
      }
    }

    checkExistingAuth()
  }, [return_to])

  const onSubmit = async (formData: LoginFormData) => {
    setError('')

    try {
      const response = await fetch('/patients/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (response.ok) {
        patientAuthService.handleLoginResponse(data)
        // Redirect to patient progress page
        const redirectUrl = return_to || '/patients/progress'
        router.visit(redirectUrl, { replace: true })
      } else {
        setError(data.error || 'Login failed')
      }
    } catch (err) {
      setError('An error occurred during login')
    }
  }

  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Patient Portal</CardTitle>
          <CardDescription>Sign in to view your care progress</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email or Phone Number</Label>
              <Input
                id="email"
                type="text"
                placeholder="patient@example.com or +1234567890"
                {...register('email')}
              />
              {formErrors.email && (
                <p className="text-sm text-destructive">{formErrors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                {...register('password')}
              />
              {formErrors.password && (
                <p className="text-sm text-destructive">{formErrors.password.message}</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </Button>

            <div className="mt-4 rounded-md bg-blue-50 p-3 text-sm text-blue-900">
              <p className="font-medium">For emergencies, call 911</p>
              <p className="mt-1 text-xs">This portal is for viewing your care progress only.</p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
